import * as React from "react";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import { useState, useEffect, useMemo } from "react";

export default function CheckboxList(props) {
  const { id, options, selectedValues, onChange } = props;
  const [checked, setChecked] = useState([]);
  const [isImplicitAll, setIsImplicitAll] = useState(false);

  const allOptionValues = useMemo(
    () => options.map((opt) => opt.value),
    [options],
  );
  const allChecked = options.length > 0 && checked.length === options.length;
  const isIndeterminate = checked.length > 0 && checked.length < options.length;

  useEffect(() => {
    const hasSelection = Array.isArray(selectedValues) && selectedValues.length > 0;
    const selected = hasSelection ? selectedValues : allOptionValues;
    setChecked(selected);
    setIsImplicitAll(!hasSelection);
  }, [selectedValues, allOptionValues]);

  const handleAllToggle = () => {
    const shouldClearAll = allChecked || isIndeterminate;
    const selectedItems = shouldClearAll ? [] : allOptionValues;
    setChecked(selectedItems);
    setIsImplicitAll(false);
    onChange(selectedItems);
  };

  const handleToggle = (value) => () => {
    if (isImplicitAll) {
      const selectedItems = [value];
      setChecked(selectedItems);
      setIsImplicitAll(false);
      onChange(selectedItems);
      return;
    }

    const newChecked = checked.includes(value)
      ? checked.filter((item) => item !== value)
      : [...checked, value];
    setChecked(newChecked);
    setIsImplicitAll(false);
    onChange(newChecked);
  };

  return (
    <List
      sx={{
        width: "100%",
        maxWidth: 360,
        bgcolor: "background.paper",
        padding: 0,
        margin: 0,
      }}
    >
      <ListItem key="all-option" disablePadding>
        <ListItemButton role={undefined} onClick={handleAllToggle} dense>
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={allChecked}
              indeterminate={isIndeterminate}
              tabIndex={-1}
              disableRipple
              inputProps={{ "aria-labelledby": `all-option-${id}` }}
            />
          </ListItemIcon>
          <ListItemText
            id={`all-option-${id}`}
            primary="All"
            primaryTypographyProps={{
              style: {
                wordBreak: "break-word",
                overflowWrap: "break-word",
              },
            }}
          />
        </ListItemButton>
      </ListItem>
      {options.map((option, index) => {
        const labelId = `checkbox-list-${id}-${option.value}`;
        const optionKey = `checkbox-list-item-${id}-${String(option.value)}-${index}`;
        return (
          <ListItem key={optionKey} disablePadding>
            <ListItemButton
              role={undefined}
              onClick={handleToggle(option.value)}
              dense
            >
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={checked.includes(option.value)}
                  tabIndex={-1}
                  inputProps={{ "aria-labelledby": labelId }}
                  data-testid={`checkbox-list-item-${id}-${option.value}`}
                />
              </ListItemIcon>
              <ListItemText
                id={labelId}
                primary={option.label}
                primaryTypographyProps={{
                  style: {
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}

CheckboxList.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedValues: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};
